import React, { useState, useEffect } from "react";
import { Sparkles, CheckCircle2, ChevronRight, Play, AlertCircle, ArrowRight, Video, Calendar, ShieldCheck, Quote } from "lucide-react";
import { CONFIG } from "../config";
import SEO from "../components/SEO";
import RegistrationModal from "../components/RegistrationModal";
import { useNavigate } from "react-router-dom";

export default function LandingPage() {
  const [modalOpen, setModalOpen] = useState(false);
  const [showFloatingCTA, setShowFloatingCTA] = useState(false);
  const navigate = useNavigate();

  // Handle scroll to show mobile floating CTA
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 500) {
        setShowFloatingCTA(true);
      } else {
        setShowFloatingCTA(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleRegisterSuccess = () => {
    setModalOpen(false);
    navigate("/thank-you");
  };

  // Mock FAQs
  const faqs = [
    {
      q: "Is this masterclass truly free?",
      a: "Yes, completely free. No credit card is required. Our mission is to share the Knowledge-to-Income system so you can see how AI structures your existing expertise into a highly profitable digital coaching business."
    },
    {
      q: "What if I can't attend live at the scheduled time?",
      a: "While live attendance is highly recommended for the interactive coaching, registering guarantees you private access to the webinar replay room for 48 hours following the event."
    },
    {
      q: "Do I need to be a technical or AI expert to do this?",
      a: "Absolutely not. In fact, this system is designed specifically to remove technical friction. We show you how to write simple prompts to get world-class results, with no coding or complex integration required."
    },
    {
      q: "Who is Meenu Soni?",
      a: "Meenu Soni is an AI Coach & Digital Business Mentor dedicated to helping knowledge-driven people turn their expertise into clear offers, premium digital products, and automated business systems."
    }
  ];

  const [activeFaq, setActiveFaq] = useState(null);

  return (
    <div className="relative min-h-screen bg-slate-50 font-sans text-slate-800">
      <SEO 
        title="Turn Your Knowledge into a Structured Business with AI | Meenu Soni"
        description="Discover how to turn your knowledge, skills and experience into a clear coaching offer, digital product and AI-powered business system. Register for the free masterclass."
      />

      {/* Urgency Announcement Banner */}
      <div className="bg-slate-900 text-white border-b border-teal-500/20 py-2.5 px-4 sticky top-0 z-40 text-center">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-xs sm:text-sm font-semibold">
          <div className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-full bg-red-500 animate-ping"></span>
            <span className="text-teal-400 uppercase tracking-wider font-extrabold text-[10px] sm:text-xs bg-teal-500/10 px-2 py-0.5 rounded border border-teal-500/20">LIVE EVENT SECURING</span>
          </div>
          <span className="text-slate-200">Free Webinar Registration Closes Soon</span>
          <span className="hidden sm:inline text-slate-400">|</span>
          <span className="text-teal-300 flex items-center gap-1">
            <Calendar className="w-3.5 h-3.5" /> Next Masterclass Starts: {CONFIG.defaultWebinarDate} at {CONFIG.defaultWebinarTime}
          </span>
        </div>
      </div>

      {/* Main Container */}
      <main className="pb-24">
        
        {/* HERO SECTION */}
        <section className="relative pt-12 pb-20 sm:pt-16 sm:pb-28 overflow-hidden bg-gradient-to-br from-teal-50/40 via-white to-sky-50/30 border-b border-slate-100">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
            
            {/* Top Badge */}
            <div className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-teal-500/10 text-teal-800 text-xs sm:text-sm font-bold border border-teal-500/20 mb-6 tracking-wide uppercase">
              <Sparkles className="w-4 h-4 text-teal-600 animate-pulse" />
              <span>FREE WEBINAR MASTERCLASS</span>
            </div>

            {/* Main Transformational Hook */}
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-slate-950 leading-[1.12] mb-6 max-w-4xl mx-auto">
              TURN YOUR KNOWLEDGE INTO A <span className="text-transparent bg-clip-text bg-gradient-to-r from-teal-600 to-sky-600">STRUCTURED BUSINESS</span> WITH AI
            </h1>

            {/* Core Message / Supporting Paragraph */}
            <p className="text-lg sm:text-xl text-slate-600 max-w-2xl mx-auto leading-relaxed mb-8">
              Your knowledge is already an asset. AI can help you turn your expertise into a clear niche, a valuable offer, and a simple digital business system.
            </p>

            {/* Webinar Name & System Branding */}
            <div className="bg-white/80 backdrop-blur-sm border border-slate-200/80 rounded-2xl p-6 sm:p-8 max-w-3xl mx-auto shadow-sm mb-10 flex flex-col md:flex-row md:items-center justify-between gap-6 text-left">
              <div>
                <span className="text-xs font-bold uppercase tracking-wider text-teal-600">Webinar Focus:</span>
                <h2 className="text-xl sm:text-2xl font-extrabold text-slate-900 mt-1">
                  {CONFIG.webinarTitle}
                </h2>
                <p className="text-sm font-semibold text-slate-500 mt-1 flex items-center gap-1">
                  <span className="inline-block w-2 h-2 rounded-full bg-sky-500"></span>
                  {CONFIG.webinarSubtitle}
                </p>
                <p className="text-xs sm:text-sm text-slate-500 mt-3 leading-relaxed max-w-lg">
                  A free masterclass for coaches, consultants, experts, creators and professionals who want to turn what they know into something they can build, offer and sell online.
                </p>
              </div>

              <div className="flex-shrink-0 flex flex-col gap-2">
                <button 
                  onClick={() => setModalOpen(true)}
                  className="w-full md:w-auto px-8 py-4 bg-teal-600 hover:bg-teal-700 text-white font-extrabold rounded-xl transition-all shadow-md active:scale-[0.98] flex items-center justify-center gap-2 group tracking-wide text-sm sm:text-base cursor-pointer"
                  data-testid="hero-register-cta-button"
                >
                  RESERVE MY FREE SEAT
                  <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
                <div className="flex items-center justify-center md:justify-start gap-1.5 text-xs text-slate-500 font-semibold mt-1">
                  <ShieldCheck className="w-4 h-4 text-emerald-500" />
                  <span>No credit card required</span>
                </div>
              </div>
            </div>

            {/* Credibility points */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 max-w-3xl mx-auto pt-4 border-t border-slate-100">
              {[
                { label: "100% Free Live Access", value: "Live Event" },
                { label: "Done-With-You System", value: "Proven Blueprint" },
                { label: "Direct Q&A Session", value: "Interactive" },
                { label: "Exclusive Live Gift", value: "Launch Cheatsheet" }
              ].map((item, idx) => (
                <div key={idx} className="text-center sm:text-left sm:pl-4 border-l border-slate-100">
                  <p className="text-xs text-slate-400 uppercase tracking-widest font-semibold">{item.value}</p>
                  <p className="text-sm font-bold text-slate-800 mt-0.5">{item.label}</p>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* TRUST BAR SECTION */}
        <section className="py-8 bg-white border-b border-slate-100">
          <div className="max-w-7xl mx-auto px-4 text-center">
            <p className="text-xs uppercase tracking-widest font-extrabold text-slate-400 mb-5">
              HELPING LEADERS & PROFESSIONALS WITH BACKGROUNDS FROM:
            </p>
            <div className="flex flex-wrap items-center justify-center gap-6 sm:gap-12 opacity-45 grayscale contrast-200">
              <span className="font-extrabold text-lg sm:text-xl tracking-tight text-slate-700">GOOGLE</span>
              <span className="font-extrabold text-lg sm:text-xl tracking-tight text-slate-700">MCKINSEY</span>
              <span className="font-extrabold text-lg sm:text-xl tracking-tight text-slate-700">MICROSOFT</span>
              <span className="font-extrabold text-lg sm:text-xl tracking-tight text-slate-700">META</span>
              <span className="font-extrabold text-lg sm:text-xl tracking-tight text-slate-700">DELOITTE</span>
            </div>
          </div>
        </section>

        {/* CORE PROBLEM SECTION */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <span className="text-xs uppercase tracking-wider font-extrabold text-sky-600 bg-sky-50 px-3 py-1 rounded-full">THE ROOT PROBLEM</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-950 mt-4 mb-6 leading-tight">
              YOU DON'T NEED MORE INFORMATION. <span className="text-transparent bg-clip-text bg-gradient-to-r from-sky-600 to-teal-600">YOU NEED CLARITY.</span>
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mb-12 max-w-2xl mx-auto">
              You already possess valuable knowledge and real-world experience. Yet, when you try to structure it into a digital asset, you run into the same recurring blockers:
            </p>

            {/* Key Blockers Bento Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 text-left">
              {[
                { title: "Too Many Ideas", desc: "You have massive knowledge but struggle to narrow down a singular high-impact focus." },
                { title: "AI Tool Overwhelm", desc: "Hundreds of tools with no clear workflow, leaving you confused on what works." },
                { title: "No Clear Niche", desc: "Unsure who your ideal high-ticket buyer is and how to market directly to them." },
                { title: "No Structured Offer", desc: "Unsure how to frame your advice into a scalable curriculum, community, or cohort." },
                { title: "Inconsistent Leads", desc: "Relying on word-of-mouth rather than a predictable, automated pipeline." },
                { title: "Tech Setup Friction", desc: "Complex tools, landing page builders, and servers keep you from launching." }
              ].map((item, idx) => (
                <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-200/60 shadow-sm flex gap-4">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-rose-50 flex items-center justify-center text-rose-500 font-bold text-sm">
                    ✖
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-900 text-base">{item.title}</h3>
                    <p className="text-sm text-slate-500 mt-1 leading-relaxed">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-12 bg-teal-50/40 border border-teal-100 rounded-2xl p-6 sm:p-8 flex items-start gap-4 text-left max-w-2xl mx-auto">
              <AlertCircle className="w-6 h-6 text-teal-600 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-bold text-teal-900 text-sm sm:text-base">The AI Advantage Difference</h4>
                <p className="text-xs sm:text-sm text-teal-700 mt-1.5 leading-relaxed">
                  We don't teach "passive income get-rich schemes." We show you how AI acts as an executive assistant to synthesize what you know, package it properly, and setup an elegant client-acquisition vehicle.
                </p>
              </div>
            </div>

          </div>
        </section>

        {/* 3 BREAKTHROUGH SECRETS SECTION */}
        <section className="py-20 bg-white border-t border-b border-slate-100">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-wider font-extrabold text-teal-600 bg-teal-50 px-3 py-1 rounded-full">INSIDE THIS FREE MASTERCLASS</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-950 mt-4 leading-tight">
                THE 3 BREAKTHROUGHS YOU WILL DISCOVER
              </h2>
              <p className="text-sm sm:text-base text-slate-500 mt-3 max-w-xl mx-auto">
                How our Knowledge-to-Income System breaks down the complex journey into simple, repeatable milestones using AI.
              </p>
            </div>

            {/* Bento Secrets Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              
              {/* Secret 1 */}
              <div 
                className="bg-slate-50 hover:bg-slate-50/50 p-8 rounded-2xl border border-slate-200/80 transition-all group flex flex-col justify-between"
                data-testid="secrets-card-1"
              >
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-teal-600 bg-teal-50 px-3 py-1 rounded-full inline-block mb-6">
                    SECRET 1
                  </span>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-950 leading-snug group-hover:text-teal-600 transition-colors">
                    Find Your Profitable Coaching Niche With AI
                  </h3>
                  <p className="text-sm text-slate-500 mt-4 leading-relaxed">
                    Stop competing in crowded markets. Discover how to instruct AI to cross-reference your expertise, real-world problems, and audience purchasing power to land on a high-value niche.
                  </p>
                </div>
                <div className="mt-8 pt-6 border-t border-slate-200/60 text-xs text-slate-400 font-semibold uppercase tracking-wider">
                  🔑 Key Takeaway: The AI Niche Matrix
                </div>
              </div>

              {/* Secret 2 */}
              <div 
                className="bg-slate-50 hover:bg-slate-50/50 p-8 rounded-2xl border border-slate-200/80 transition-all group flex flex-col justify-between"
                data-testid="secrets-card-2"
              >
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-sky-600 bg-sky-50 px-3 py-1 rounded-full inline-block mb-6">
                    SECRET 2
                  </span>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-950 leading-snug group-hover:text-sky-600 transition-colors">
                    Turn Your Knowledge Into a Scalable Digital Product
                  </h3>
                  <p className="text-sm text-slate-500 mt-4 leading-relaxed">
                    Transform your loose ideas and stories into premium digital assets. Learn how to map your coaching offers, courses, community platforms, live workshops, or challenge frameworks using AI templates.
                  </p>
                </div>
                <div className="mt-8 pt-6 border-t border-slate-200/60 text-xs text-slate-400 font-semibold uppercase tracking-wider">
                  📦 Key Takeaway: The Offering Map
                </div>
              </div>

              {/* Secret 3 */}
              <div 
                className="bg-slate-50 hover:bg-slate-50/50 p-8 rounded-2xl border border-slate-200/80 transition-all group flex flex-col justify-between"
                data-testid="secrets-card-3"
              >
                <div>
                  <span className="text-xs font-bold uppercase tracking-wider text-teal-600 bg-teal-50 px-3 py-1 rounded-full inline-block mb-6">
                    SECRET 3
                  </span>
                  <h3 className="text-lg sm:text-xl font-bold text-slate-950 leading-snug group-hover:text-teal-600 transition-colors">
                    Build a Simple Video Money Funnel
                  </h3>
                  <p className="text-sm text-slate-500 mt-4 leading-relaxed">
                    No complex tech. Discover how a single, high-converting video funnel paired with simple landing pages builds immense trust, filters prospects, and handles automated / autopilot sales journeys 24/7.
                  </p>
                </div>
                <div className="mt-8 pt-6 border-t border-slate-200/60 text-xs text-slate-400 font-semibold uppercase tracking-wider">
                  ⚙️ Key Takeaway: The Autopilot Funnel
                </div>
              </div>

            </div>

            {/* Quick Summary Outcome Banner */}
            <div className="mt-16 bg-slate-950 text-white rounded-3xl p-8 sm:p-10 text-center relative overflow-hidden">
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(20,184,166,0.15),transparent)]"></div>
              <h3 className="text-xl sm:text-2xl font-bold tracking-tight mb-4 relative z-10">
                Knowledge <strong className="text-teal-400 font-normal">→</strong> Clarity <strong className="text-teal-400 font-normal">→</strong> Offer <strong className="text-teal-400 font-normal">→</strong> Digital Product <strong className="text-teal-400 font-normal">→</strong> Automated Sales
              </h3>
              <p className="text-sm text-slate-400 max-w-2xl mx-auto leading-relaxed mb-6 relative z-10">
                This isn't a complex web of tactics. It's a clean, automated pipeline that turns what you already know into a professional coaching asset.
              </p>
              <button 
                onClick={() => setModalOpen(true)}
                className="relative z-10 px-8 py-3.5 bg-teal-500 hover:bg-teal-400 text-slate-950 font-bold rounded-xl transition-all shadow-lg text-sm sm:text-base cursor-pointer"
              >
                RESERVE MY FREE SEAT
              </button>
            </div>

          </div>
        </section>

        {/* TARGET AUDIENCE SECTION */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-5xl mx-auto px-4">
            <div className="text-center mb-16">
              <span className="text-xs uppercase tracking-wider font-extrabold text-sky-600 bg-sky-50 px-3 py-1 rounded-full">WHO IS THIS FOR?</span>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-950 mt-4 leading-tight">
                THIS IS DESIGNED FOR YOU IF YOU ARE:
              </h2>
              <p className="text-sm sm:text-base text-slate-500 mt-2 max-w-xl mx-auto">
                No matter your industry, if you carry hard-won experience and professional knowledge, this free system applies to you.
              </p>
            </div>

            {/* Compact Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              {[
                { title: "Coach", subtitle: "Wants premium structures" },
                { title: "Consultant", subtitle: "Bypassing hourly work" },
                { title: "Trainer", subtitle: "Scaling curriculum" },
                { title: "Expert", subtitle: "Packing experience" },
                { title: "Creator", subtitle: "Monetizing reach" },
                { title: "Educator", subtitle: "Modernizing classrooms" },
                { title: "Professional", subtitle: "Structuring corporate skills" },
                { title: "Aspiring Brand", subtitle: "Establishing authority" }
              ].map((item, idx) => (
                <div key={idx} className="bg-white p-6 rounded-2xl border border-slate-200/50 shadow-sm flex flex-col justify-center items-center">
                  <div className="w-10 h-10 rounded-full bg-teal-50 text-teal-600 flex items-center justify-center font-bold mb-3">
                    ✓
                  </div>
                  <h3 className="font-extrabold text-slate-900 text-base">{item.title}</h3>
                  <p className="text-xs text-slate-400 mt-1">{item.subtitle}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* SPEAKER PROFILE SECTION */}
        <section className="py-20 bg-white border-t border-b border-slate-100" id="host">
          <div className="max-w-4xl mx-auto px-4">
            <div 
              className="bg-slate-50 rounded-3xl border border-slate-200/60 p-8 sm:p-12"
              data-testid="speaker-profile-card"
            >
              <div className="grid grid-cols-1 md:grid-cols-12 gap-8 items-center">
                
                {/* Photo */}
                <div className="md:col-span-5 relative">
                  <div className="w-full aspect-[4/5] rounded-2xl overflow-hidden bg-slate-200 border border-slate-200 shadow-md relative">
                    <img 
                      src="https://tagmango.com/assets/chatgpt-image-jun-26-2026-030221-pm-229d7d3a0c13df5d0a50bb5692b4c3f1.png"
                      alt="Meenu Soni"
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-3 left-3 bg-slate-950/80 backdrop-blur-md px-3 py-1 rounded-full text-[10px] text-teal-400 font-bold uppercase tracking-widest border border-teal-500/20">
                      Primary Host
                    </div>
                  </div>
                </div>

                {/* Info & Text */}
                <div className="md:col-span-7 flex flex-col justify-center">
                  <span className="text-xs font-bold text-teal-600 tracking-wider uppercase mb-1.5">Your Masterclass Host</span>
                  <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-950 leading-tight">
                    {CONFIG.creatorName}
                  </h2>
                  <p className="text-sm font-semibold text-slate-500 mt-1">
                    {CONFIG.creatorTitle}
                  </p>

                  <div className="h-px bg-slate-200 my-5"></div>

                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                    Helping knowledge-driven people turn their expertise into clear offers, digital products and AI-powered business systems.
                  </p>

                  <p className="text-sm sm:text-base text-slate-600 leading-relaxed mt-4">
                    Meenu believes that everyone possesses valuable experience that someone else is searching for right now. Through practical, educational framework tutorials and cutting-edge AI systems, she helps educators and professionals launch clear, structured business funnels.
                  </p>

                  {/* Trust highlight block */}
                  <div className="mt-6 p-4 bg-white rounded-xl border border-slate-100 flex items-center gap-3">
                    <CheckCircle2 className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                    <span className="text-xs sm:text-sm font-semibold text-slate-700">Fully Educational, No False Hype, Practical AI Implementation</span>
                  </div>

                </div>

              </div>
            </div>
          </div>
        </section>

        {/* FAQ ACCORDION SECTION */}
        <section className="py-20 bg-slate-50">
          <div className="max-w-3xl mx-auto px-4">
            <div className="text-center mb-12">
              <span className="text-xs uppercase tracking-wider font-extrabold text-sky-600 bg-sky-50 px-3 py-1 rounded-full">COMMON QUESTIONS</span>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-slate-950 mt-4 leading-tight">
                FREQUENTLY ASKED QUESTIONS
              </h2>
            </div>

            <div className="space-y-4">
              {faqs.map((faq, idx) => (
                <div key={idx} className="bg-white rounded-2xl border border-slate-200/60 shadow-sm overflow-hidden">
                  <button 
                    onClick={() => setActiveFaq(activeFaq === idx ? null : idx)}
                    className="w-full px-6 py-5 text-left font-bold text-slate-900 flex items-center justify-between gap-4 transition-colors hover:text-teal-600"
                  >
                    <span className="text-sm sm:text-base">{faq.q}</span>
                    <span className="text-slate-400 font-mono text-xl flex-shrink-0">
                      {activeFaq === idx ? "−" : "+"}
                    </span>
                  </button>
                  {activeFaq === idx && (
                    <div className="px-6 pb-5 text-sm sm:text-base text-slate-500 leading-relaxed border-t border-slate-100 pt-3">
                      {faq.a}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FINAL CTA SECTION */}
        <section className="py-20 bg-white border-t border-slate-100">
          <div className="max-w-4xl mx-auto px-4 text-center">
            <span className="text-xs uppercase tracking-wider font-extrabold text-teal-600 bg-teal-50 px-3 py-1 rounded-full">TAKE ACTION TODAY</span>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-950 mt-4 mb-3 leading-tight uppercase">
              YOUR KNOWLEDGE IS VALUABLE.<br/>NOW GIVE IT A STRUCTURE.
            </h2>
            <p className="text-base sm:text-lg text-slate-600 mb-8 max-w-2xl mx-auto leading-relaxed">
              Join the {CONFIG.webinarTitle} and discover how AI can help you turn your knowledge into a clear, structured digital business.
            </p>

            <button 
              onClick={() => setModalOpen(true)}
              className="px-10 py-5 bg-teal-600 hover:bg-teal-700 text-white font-extrabold rounded-xl transition-all shadow-lg hover:shadow-teal-100 text-base sm:text-lg tracking-wide uppercase active:scale-[0.98] inline-flex items-center gap-3 cursor-pointer"
            >
              RESERVE MY FREE SEAT
              <ArrowRight className="w-5 h-5" />
            </button>
            
            <p className="text-xs text-slate-400 mt-4 font-semibold uppercase tracking-widest flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-emerald-500" />
              100% Free Live Presentation • Replay Room Guaranteed
            </p>
          </div>
        </section>

      </main>

      {/* FOOTER */}
      <footer className="bg-slate-900 text-slate-400 border-t border-slate-800 py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center sm:text-left">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-6 pb-8 border-b border-slate-800">
            <div>
              <p className="text-lg font-bold text-white tracking-wider uppercase">
                {CONFIG.creatorName}
              </p>
              <p className="text-xs text-slate-500 mt-0.5">
                {CONFIG.creatorTitle}
              </p>
            </div>
            <div className="flex gap-6 text-sm">
              <a href="#host" className="hover:text-white transition-colors">About Host</a>
              <button onClick={() => setModalOpen(true)} className="hover:text-white transition-colors">Register Free</button>
            </div>
          </div>
          <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
            <p>© 2026 {CONFIG.creatorName}. All rights reserved.</p>
            <p className="mt-1 sm:mt-0">
              This site belongs to creator: {CONFIG.creatorName} ({CONFIG.domain}). Designed by Mango AI.
            </p>
          </div>
        </div>
      </footer>

      {/* MOBILE FLOATING STICKY CTA */}
      {showFloatingCTA && (
        <div className="fixed bottom-0 left-0 right-0 z-40 bg-white/95 backdrop-blur-md border-t border-slate-200/80 p-3 sm:hidden animate-slide-up flex items-center justify-between gap-4 shadow-xl">
          <div className="pl-1">
            <p className="text-xs font-bold text-slate-900 leading-tight uppercase truncate">{CONFIG.webinarTitle}</p>
            <p className="text-[10px] text-teal-600 font-semibold mt-0.5">{CONFIG.defaultWebinarDate}</p>
          </div>
          <button 
            onClick={() => setModalOpen(true)}
            className="flex-shrink-0 px-4 py-2.5 bg-teal-600 hover:bg-teal-700 text-white font-bold rounded-lg text-xs tracking-wide active:scale-[0.98] cursor-pointer"
          >
            RESERVE SEAT
          </button>
        </div>
      )}

      {/* REGISTRATION MODAL */}
      <RegistrationModal 
        isOpen={modalOpen} 
        onClose={() => setModalOpen(false)} 
        onRegisterSuccess={handleRegisterSuccess}
      />
    </div>
  );
}
